/**
 * Fetch-based HTTP client matching previous axios defaults:
 * - credentials: 'include' (same as axios.defaults.withCredentials)
 * - JSON request/response
 * - FormData without manual Content-Type (browser sets multipart boundary)
 * - Errors shaped like axios: error.response.{ status, data }
 */

export class HttpError extends Error {
  readonly response: {
    status: number;
    data: unknown;
    headers: Headers;
  };

  constructor(message: string, status: number, data: unknown, headers: Headers) {
    super(message);
    this.name = 'HttpError';
    this.response = { status, data, headers };
  }
}

/** Type guard compatible with code that used axios.isAxiosError + error.response */
export function isApiError(error: unknown): error is HttpError {
  return error instanceof HttpError;
}

/** Parse response body: JSON when possible, else raw string, empty -> undefined */
export async function parseResponseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return undefined;
  const trimmed = text.trim();
  if (
    (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
    (trimmed.startsWith('[') && trimmed.endsWith(']'))
  ) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

function stripContentTypeForFormData(headers: Headers, body: BodyInit | null | undefined): void {
  if (body instanceof FormData) {
    headers.delete('Content-Type');
  }
}

/**
 * Low-level fetch with credentials included (SuperTokens patches global fetch).
 */
export async function apiRequest(url: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers);
  stripContentTypeForFormData(headers, init.body ?? null);
  return fetch(url, {
    ...init,
    credentials: init.credentials ?? 'include',
    headers,
  });
}

export async function requestJson<T>(url: string, init: RequestInit): Promise<T> {
  const response = await apiRequest(url, init);
  const data = await parseResponseBody(response);
  if (!response.ok) {
    throw new HttpError(response.statusText || 'Request failed', response.status, data, response.headers);
  }
  return data as T;
}

export async function requestVoid(url: string, init: RequestInit): Promise<void> {
  const response = await apiRequest(url, init);
  if (!response.ok) {
    const data = await parseResponseBody(response);
    throw new HttpError(response.statusText || 'Request failed', response.status, data, response.headers);
  }
}

export function getJson<T>(url: string): Promise<T> {
  return requestJson<T>(url, { method: 'GET' });
}

export function postJson<T>(url: string, body: unknown): Promise<T> {
  return requestJson<T>(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function patchJson<T>(url: string, body: unknown): Promise<T> {
  return requestJson<T>(url, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function putJson<T>(url: string, body: unknown): Promise<T> {
  return requestJson<T>(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export function postFormData<T>(url: string, formData: FormData): Promise<T> {
  return requestJson<T>(url, {
    method: 'POST',
    body: formData,
  });
}

export function putFormData<T>(url: string, formData: FormData): Promise<T> {
  return requestJson<T>(url, {
    method: 'PUT',
    body: formData,
  });
}

export function patchFormData<T>(url: string, formData: FormData): Promise<T> {
  return requestJson<T>(url, {
    method: 'PATCH',
    body: formData,
  });
}

export function deleteJson<T>(url: string, jsonBody?: unknown): Promise<T> {
  const init: RequestInit = {
    method: 'DELETE',
  };
  if (jsonBody !== undefined) {
    init.headers = { 'Content-Type': 'application/json' };
    init.body = JSON.stringify(jsonBody);
  }
  return requestJson<T>(url, init);
}

/** DELETE with query string only */
export function deleteJsonQuery<T>(url: string, query: Record<string, string>): Promise<T> {
  const qs = new URLSearchParams(query).toString();
  const fullUrl = qs ? `${url}?${qs}` : url;
  return requestJson<T>(fullUrl, { method: 'DELETE' });
}

export async function deleteNoBody(url: string): Promise<void> {
  await requestVoid(url, { method: 'DELETE' });
}

/** DELETE that returns success boolean when server responds 204 */
export async function deleteExpect204(url: string): Promise<boolean> {
  const response = await apiRequest(url, { method: 'DELETE' });
  if (!response.ok) {
    const data = await parseResponseBody(response);
    throw new HttpError(response.statusText || 'Request failed', response.status, data, response.headers);
  }
  return response.status === 204;
}
